import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { modulesAPI } from "../../services/api";
import VideoUpload from "./VideoUpload";
import {
  Box, Typography, Button, Card, Chip, Stepper,
  Step, StepLabel, TextField, Select, MenuItem, FormControl, InputLabel,
  Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Accordion, AccordionSummary, AccordionDetails, Grid
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import Switch from "@mui/material/Switch";

const steps = ["Create Module", "Add Media", "Add Transcript", "Publish"];

export default function ModuleBuilder() {
  const { user } = useAuth();
  const [paths, setPaths] = useState([]);
  const [selectedPath, setSelectedPath] = useState("");
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [open, setOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [previewModule, setPreviewModule] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [createdModule, setCreatedModule] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [certifiedWarning, setCertifiedWarning] = useState({ open: false, count: 0 });
  const [dialogError, setDialogError] = useState("");
  const [pathCertifiedCount, setPathCertifiedCount] = useState(0);
  const [uploadNewVideo, setUploadNewVideo] = useState(false);
  const [editModule, setEditModule] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editVideoForm, setEditVideoForm] = useState({});
  const [editTranscriptForm, setEditTranscriptForm] = useState({});
  const [moduleForm, setModuleForm] = useState({
    module_title: "", description: "",
    sequence_order: 1, pass_threshold: 80,
    max_retakes: 3, cooldown_hours: 24, is_mandatory: true
  });
  const [videoForm, setVideoForm] = useState({
    video_title: "", storage_type: "local",
    storage_url: "", duration_seconds: 0
  });
  const [transcriptForm, setTranscriptForm] = useState({
    content: "", source_type: "manual"
  });

  useEffect(() => { loadPaths(); }, []);
  useEffect(() => {
    if (selectedPath) {
      loadModules(selectedPath);
      modulesAPI.getCertifiedCount(selectedPath)
        .then(res => setPathCertifiedCount(res.data.certified_count || 0))
        .catch(() => setPathCertifiedCount(0));
    }
  }, [selectedPath]);

  const loadPaths = async () => {
    try {
      const res = await modulesAPI.getPaths();
      setPaths(res.data);
      if (res.data.length > 0) setSelectedPath(res.data[0].path_id);
    } catch { setError("Failed to load paths."); }
    finally { setLoading(false); }
  };

  const loadModules = async (pathId) => {
    try {
      const res = await modulesAPI.getModules(pathId);
      setModules(res.data);
    } catch {}
  };

  const resetCreateForm = () => {
    setActiveStep(0);
    setCreatedModule(null);
    setModuleForm({ module_title: "", description: "", sequence_order: 1, pass_threshold: 80, max_retakes: 3, cooldown_hours: 24, is_mandatory: true });
    setVideoForm({ video_title: "", storage_type: "local", storage_url: "", duration_seconds: 0 });
    setTranscriptForm({ content: "", source_type: "manual" });
    setDialogError("");
  };

  const handleCancelCreate = async () => {
    if (createdModule) {
      try { await modulesAPI.deleteModule(createdModule.module_id); } catch {}
    }
    setOpen(false);
    setSuccess("");
    setError("");
    resetCreateForm();
    loadModules(selectedPath);
  };

  const handleCreateModule = async () => {
    if (!moduleForm.module_title.trim()) { setError("Module title is required."); return; }
    try {
      const res = await modulesAPI.createModule({ ...moduleForm, path_id: selectedPath });
      setCreatedModule(res.data);
      setActiveStep(1);
    } catch (err) {
      setDialogError(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : "Failed to create module.");
    }
  };

  const handleAddVideo = async () => {
    try {
      await modulesAPI.addVideo(createdModule.module_id, videoForm);
      setActiveStep(2);
      setSuccess("Video added. Now add a transcript.");
    } catch (err) {
      setError(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : "Failed to add video.");
    }
  };

  const handleAddTranscript = async () => {
    try {
      const res = await modulesAPI.addTranscript(createdModule.module_id, transcriptForm);
      setCreatedModule(prev => ({...prev, transcript: res.data?.transcript || transcriptForm}));
      setActiveStep(3);
      setSuccess("Transcript added. Ready to publish.");
    } catch (err) {
      setError(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : "Failed to add transcript.");
    }
  };

  const handlePublish = async () => {
    try {
      // Fetch latest module state from backend to check media/transcript
      const moduleRes = await modulesAPI.getModule(createdModule.module_id);
      const latestModule = moduleRes?.data;
      if (!latestModule?.video) {
        setDialogError("Please add media (video, audio, PDF or document) before publishing.");
        setActiveStep(1);
        return;
      }
      if (!latestModule?.transcript) {
        setDialogError("Please add a transcript before publishing.");
        setActiveStep(2);
        return;
      }
      await modulesAPI.publishModule(createdModule.module_id);
      setSuccess("Module published successfully!");
      setOpen(false);
      setActiveStep(0);
      setCreatedModule(null);
      setModuleForm({ module_title: "", description: "", sequence_order: 1, pass_threshold: 80, max_retakes: 3, cooldown_hours: 24, is_mandatory: true });
      setVideoForm({ video_title: "", storage_type: "local", storage_url: "", duration_seconds: 0 });
      setTranscriptForm({ content: "", source_type: "manual" });
      loadModules(selectedPath);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to publish module.");
    }
  };

  const handlePreview = (module) => {
    setPreviewModule(module);
    setPreviewOpen(true);
  };

  const getVideoSrc = (url) => {
    if (!url) return "";
    return url;
  };

  const handleEditOpen = (module) => {
    setEditModule(module);
    setEditForm({
      module_title: module.module_title,
      description: module.description || "",
      sequence_order: module.sequence_order,
      pass_threshold: module.pass_threshold,
      max_retakes: module.max_retakes,
      cooldown_hours: module.cooldown_hours,
      is_mandatory: module.is_mandatory,
      status: module.status,
      path_id: selectedPath
    });
    setEditVideoForm(module.video ? {
      video_title: module.video.video_title,
      storage_url: module.video.storage_url,
      storage_type: module.video.storage_type || "local",
      duration_seconds: module.video.duration_seconds || 0
    } : {});
    setEditTranscriptForm(module.transcript ? {
      content: module.transcript.content,
      source_type: module.transcript.source_type || "manual"
    } : {});
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editForm.module_title || !editForm.module_title.trim()) { setError("Module title is required."); return; }
    try {
      await modulesAPI.updateModule(editModule.module_id, editForm);
      if (editVideoForm.video_title && editVideoForm.storage_url && !uploadNewVideo) {
        await modulesAPI.updateVideo(editModule.module_id, editVideoForm);
      }
      if (editTranscriptForm.content) {
        if (editModule.transcript) {
          await modulesAPI.updateTranscript(editModule.module_id, editTranscriptForm);
        } else {
          await modulesAPI.addTranscript(editModule.module_id, editTranscriptForm);
        }
      }
      setSuccess("Module updated successfully!");
      setEditOpen(false);
      loadModules(selectedPath);
    } catch (err) {
      setError(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : "Failed to update module.");
    }
  };

  const filteredModules = modules.filter(m => m.module_title !== "Final Exam").filter(m => {
    const matchSearch = search === "" || m.module_title.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === "all" || m.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const statusColor = { draft: "default", active: "success", archived: "error" };

  const getDialogTitle = () => {
    if (activeStep === 0) return "Create New Module";
    if (activeStep === 1) return "Add Media";
    if (activeStep === 2) return "Add Transcript";
    return "Publish Module";
  };

  const handleDeleteModule = async (moduleId, moduleTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${moduleTitle}"? This cannot be undone.`)) return;
    try {
      await modulesAPI.deleteModule(moduleId);
      setSuccess(`Module "${moduleTitle}" deleted successfully.`);
      loadModules(selectedPath);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to delete module.");
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} color="#1a237e">Module Builder</Typography>
          <Typography variant="body2" color="text.secondary">Create and manage training modules</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={async () => {
            if (selectedPath) {
              try {
                const res = await modulesAPI.getCertifiedCount(selectedPath);
                if (res.data && res.data.certified_count > 0) {
                  setError(`Cannot add modules — ${res.data.certified_count} agent(s) are certified in this learning path.`);
                  return;
                }
              } catch {}
            }
            resetCreateForm();
            setOpen(true);
          }}
          disabled={paths.length === 0}
          sx={{ background: "linear-gradient(135deg, #1a237e, #0d47a1)" }}>
          New Module
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>{success}</Alert>}

      {paths.length > 0 && (
        <FormControl sx={{ mb: 3, minWidth: 300 }}>
          <InputLabel>Select Learning Path</InputLabel>
          <Select value={selectedPath} label="Select Learning Path"
            onChange={e => { setSelectedPath(e.target.value); setError(""); setSuccess(""); }}>
            {paths.map(p => <MenuItem key={p.path_id} value={p.path_id}>{p.path_name}</MenuItem>)}
          </Select>
        </FormControl>
      )}

      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <TextField placeholder="Search modules..." value={search}
          onChange={e => setSearch(e.target.value)} size="small" sx={{ flexGrow: 1 }} />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filterStatus} label="Status" onChange={e => setFilterStatus(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="archived">Archived</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}><CircularProgress /></Box>
      ) : modules.length === 0 ? (
        <Card sx={{ borderRadius: 3, textAlign: "center", p: 4 }}>
          <VideoLibraryIcon sx={{ fontSize: 64, color: "#ccc", mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No modules yet</Typography>
        </Card>
      ) : (
        filteredModules.map(module => (
          <Accordion key={module.module_id} sx={{ mb: 1, borderRadius: 2, "&:before": { display: "none" } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                <Typography fontWeight={600}>{module.module_title}</Typography>
                <Chip label={module.status} color={statusColor[module.status]} size="small" />
                {module.video && <Chip label={
                  module.video.media_type === "pdf" ? "PDF ✓" :
                  module.video.media_type === "audio" ? "Audio ✓" :
                  module.video.media_type === "text" ? "Document ✓" :
                  "Video ✓"
                } color="info" size="small" />}
                {module.transcript && <Chip label="Transcript ✓" color="info" size="small" />}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Pass Mark</Typography>
                  <Typography variant="body2" fontWeight={600}>{module.pass_threshold}%</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Max Retakes</Typography>
                  <Typography variant="body2" fontWeight={600}>{module.max_retakes}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Cooldown</Typography>
                  <Typography variant="body2" fontWeight={600}>{module.cooldown_hours}h</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Module ID</Typography>
                  <Typography variant="caption" display="block" sx={{ wordBreak: "break-all" }}>
                    {module.module_id}
                  </Typography>
                </Grid>
                {module.video && (
                  <Grid item xs={12}>
                    <Box sx={{ mt: 1, p: 2, bgcolor: "#f5f7fa", borderRadius: 2, border: "1px solid #e0e0e0" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            📹 {module.video.video_title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {module.video.storage_url}
                          </Typography>
                        </Box>
                        <Button variant="contained" startIcon={<PlayCircleIcon />}
                          onClick={() => handlePreview(module)}
                          sx={{ background: "linear-gradient(135deg, #1a237e, #0d47a1)" }}>
                          {module.video?.media_type === "pdf" ? "Preview PDF" :
                           module.video?.media_type === "audio" ? "Preview Audio" :
                           module.video?.media_type === "text" ? "View Document" :
                           "Preview Video"}
                        </Button>
                      </Box>
                    </Box>
                  </Grid>
                )}
                {module.transcript && (
                  <Grid item xs={12}>
                    <Box sx={{ p: 2, bgcolor: "#f5f7fa", borderRadius: 2, border: "1px solid #e0e0e0" }}>
                      <Typography variant="body2" fontWeight={600} mb={1}>📄 Transcript Preview</Typography>
                      <Typography variant="body2" color="text.secondary"
                        sx={{ maxHeight: 80, overflow: "hidden" }}>
                        {module.transcript.content.substring(0, 200)}...
                      </Typography>
                    </Box>
                  </Grid>
                )}
                <Grid item xs={12} sx={{ display: "flex", gap: 1 }}>
                  <Button variant="outlined" startIcon={<EditIcon />}
                    onClick={() => handleEditOpen(module)}>
                    Edit Module
                  </Button>
                  {user?.role === "admin" && (
                    <Button variant="outlined" color="error" size="small"
                      onClick={() => handleDeleteModule(module.module_id, module.module_title)}>
                      Delete
                    </Button>
                  )}
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))
      )}

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#1a237e" }}>
          {previewModule?.video?.media_type === "pdf" ? "📄" :
           previewModule?.video?.media_type === "audio" ? "🎵" :
           previewModule?.video?.media_type === "text" ? "📝" : "📹"}{" "}
          {previewModule?.video?.video_title}
        </DialogTitle>
        <DialogContent>
          {previewModule?.video && (
            previewModule.video.media_type === "pdf" ? (
              <iframe src={previewModule.video.storage_url} width="100%" height="500px"
                title={previewModule.video.video_title} style={{ border: "none", borderRadius: 8 }} />
            ) : previewModule.video.media_type === "audio" ? (
              <Box sx={{ p: 3, bgcolor: "#f5f5f5", borderRadius: 2 }}>
                <audio controls style={{ width: "100%" }}>
                  <source src={previewModule.video.storage_url} />
                  Your browser does not support the audio tag.
                </audio>
              </Box>
            ) : previewModule.video.media_type === "text" ? (
              <Box sx={{ p: 4, textAlign: "center", bgcolor: "#f5f7fa", borderRadius: 2 }}>
                <Typography variant="h2" mb={2}>📝</Typography>
                <Typography variant="body1" mb={2}>{previewModule.video.video_title}</Typography>
                <Button variant="contained" href={previewModule.video.storage_url} download
                  style={{ background: "linear-gradient(135deg, #1a237e, #0d47a1)", color: "#ffffff" }}>
                  Download Document
                </Button>
              </Box>
            ) : (
              <video controls autoPlay style={{ width: "100%", borderRadius: 8, backgroundColor: "#000", maxHeight: 480 }}>
                <source src={previewModule.video.storage_url} type={
                  previewModule.video.storage_url?.endsWith('.avi') ? 'video/x-msvideo' :
                  previewModule.video.storage_url?.endsWith('.mov') ? 'video/quicktime' :
                  previewModule.video.storage_url?.endsWith('.mpeg') ? 'video/mpeg' : 'video/mp4'
                } />
                Your browser does not support the video tag.
              </video>
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={open} onClose={handleCancelCreate} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#1a237e" }}>
          {getDialogTitle()}
        </DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} sx={{ mb: 3, mt: 1 }}>
            {steps.map(label => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
          </Stepper>


          {dialogError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDialogError("")}>{dialogError}</Alert>}
          {activeStep === 0 && (
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <TextField label="Module Title" value={moduleForm.module_title}
                onChange={e => setModuleForm({...moduleForm, module_title: e.target.value})}
                required sx={{ gridColumn: "span 2" }} />
              <TextField label="Description" value={moduleForm.description}
                onChange={e => setModuleForm({...moduleForm, description: e.target.value})}
                multiline rows={2} sx={{ gridColumn: "span 2" }} />
              <TextField label="Sequence Order" type="number" value={moduleForm.sequence_order}
                onChange={e => setModuleForm({...moduleForm, sequence_order: parseInt(e.target.value) || 1})} />
              <TextField label="Pass Threshold %" type="number" value={moduleForm.pass_threshold}
                onChange={e => setModuleForm({...moduleForm, pass_threshold: parseFloat(e.target.value) || 80})} />
              <TextField label="Max Retakes" type="number" value={moduleForm.max_retakes}
                onChange={e => setModuleForm({...moduleForm, max_retakes: parseInt(e.target.value) || 3})} />
              <TextField label="Cooldown Hours" type="number" value={moduleForm.cooldown_hours}
                onChange={e => setModuleForm({...moduleForm, cooldown_hours: parseInt(e.target.value) || 24})} />
            </Box>
          )}

          {activeStep === 1 && (
            <Box>
              <Typography variant="subtitle1" fontWeight={600} mb={2} color="#1a237e">
                Select Media Type
              </Typography>
              <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
                {[
                  { type: "video", label: "Video", icon: "🎥", accept: ".mp4,.mov" },
                  { type: "audio", label: "Audio", icon: "🎵", accept: ".mp3,.wav" },
                  { type: "pdf", label: "PDF", icon: "📄", accept: ".pdf" },
                  { type: "text", label: "Text/Word", icon: "📝", accept: ".txt,.docx,.doc" },
                  { type: "url", label: "Video URL", icon: "🔗", accept: "" },
                ].map(m => (
                  <Box key={m.type} onClick={() => setVideoForm({...videoForm, media_type: m.type})}
                    sx={{ p: 2, border: 2, borderRadius: 2, cursor: "pointer", textAlign: "center", minWidth: 80,
                      borderColor: (videoForm.media_type || "video") === m.type ? "#1a237e" : "#e0e0e0",
                      bgcolor: (videoForm.media_type || "video") === m.type ? "#e8eaf6" : "white" }}>
                    <Typography variant="h5">{m.icon}</Typography>
                    <Typography variant="caption" fontWeight={600}>{m.label}</Typography>
                  </Box>
                ))}
              </Box>
              {(videoForm.media_type || "video") === "url" ? (
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                  <TextField label="Video Title" value={videoForm.video_title}
                    onChange={e => setVideoForm({...videoForm, video_title: e.target.value})}
                    sx={{ gridColumn: "span 2" }} />
                  <TextField label="Video URL" value={videoForm.storage_url}
                    onChange={e => setVideoForm({...videoForm, storage_url: e.target.value})}
                    sx={{ gridColumn: "span 2" }}
                    helperText="e.g. https://youtube.com/... or /videos/filename.mp4" />
                </Box>
              ) : (
                <VideoUpload
                  moduleId={createdModule?.module_id}
                  onUploadComplete={(data) => {
                    setSuccess("File uploaded successfully!");
                    setCreatedModule(prev => ({...prev, video: data}));
                    setActiveStep(2);
                  }}
                />
              )}
            </Box>
          )}

          {activeStep === 2 && (
            <Box>
              <Typography variant="subtitle1" fontWeight={600} mb={2} color="#1a237e">
                Option 1 — Upload a file (TXT, PDF or Word document)
              </Typography>
              <Button variant="outlined" component="label" startIcon={<AddIcon />} sx={{ mb: 2 }}>
                Upload Transcript File
                <input type="file" hidden accept=".txt,.pdf,.doc,.docx"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    if (file.name.endsWith('.txt')) {
                      const text = await file.text();
                      setTranscriptForm({...transcriptForm, content: text, source_type: "imported"});
                    } else {
                      try {
                        const token = localStorage.getItem("token");
                        const formData = new FormData();
                        formData.append("file", file);
                        const moduleId = createdModule?.module_id;
                        if (!moduleId) { setError("Complete Step 1 first."); return; }
                        const res = await fetch(`/api/v1/upload/transcript/${moduleId}`, {
                          method: "POST",
                          headers: { "Authorization": `Bearer ${token}` },
                          body: formData
                        });
                        if (res.ok) {
                          const data = await res.json();
                          setTranscriptForm({...transcriptForm, content: data.content, source_type: "imported"});
                          setSuccess(`Transcript extracted from ${file.name}`);
                        } else { setError("Could not extract text. Please paste manually."); }
                      } catch { setError("Upload failed. Please try again."); }
                    }
                  }} />
              </Button>
              <Typography variant="subtitle1" fontWeight={600} mb={1} color="#1a237e">
                Option 2 — Type or paste content manually
              </Typography>
              <TextField fullWidth label="Transcript Content" value={transcriptForm.content}
                onChange={e => setTranscriptForm({...transcriptForm, content: e.target.value})}
                multiline rows={8} required sx={{ mb: 2 }}
                helperText="Paste or type the full written content of the module" />
              <FormControl fullWidth>
                <InputLabel>Source Type</InputLabel>
                <Select value={transcriptForm.source_type} label="Source Type"
                  onChange={e => setTranscriptForm({...transcriptForm, source_type: e.target.value})}>
                  <MenuItem value="manual">Manual (typed)</MenuItem>
                  <MenuItem value="imported">Imported from file</MenuItem>
                  <MenuItem value="speech_to_text">Speech to Text</MenuItem>
                </Select>
              </FormControl>
              <Box sx={{ mt: 2, p: 2, bgcolor: "#e3f2fd", borderRadius: 2 }}>
                <Typography variant="body2" color="#1565c0" fontWeight={600}>
                  📖 Where is the transcript used?
                </Typography>
                <Typography variant="body2" color="#1565c0" mt={0.5}>
                  Agents read the transcript on the Transcript tab of the module before taking the exam. Both the video and transcript must be completed to unlock the assessment.
                </Typography>
              </Box>
            </Box>
          )}

          {activeStep === 3 && (
            <Box sx={{ textAlign: "center", py: 3 }}>
              <CheckCircleIcon sx={{ fontSize: 80, color: "#4caf50", mb: 2 }} />
              <Typography variant="h6" fontWeight={700} mb={1}>Ready to Publish!</Typography>
              <Typography variant="body1" color="text.secondary">
                Module: <b>{createdModule?.module_title}</b>
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCancelCreate}>Cancel</Button>

          {activeStep === 0 && (
            <Button variant="contained" onClick={handleCreateModule}
              disabled={!moduleForm.module_title}
              style={{ background: "linear-gradient(135deg, #1a237e, #0d47a1)", color: "#ffffff" }}>
              Next
            </Button>
          )}
          {activeStep === 1 && (videoForm.media_type || "video") === "url" && (
            <Button variant="contained" onClick={handleAddVideo}
              disabled={!videoForm.video_title || !videoForm.storage_url}
              style={{ background: "linear-gradient(135deg, #1a237e, #0d47a1)", color: "#ffffff" }}>
              Save URL
            </Button>
          )}

          {activeStep === 2 && (
            <Button variant="contained" onClick={handleAddTranscript}
              disabled={!transcriptForm.content}
              style={{ background: "linear-gradient(135deg, #1a237e, #0d47a1)", color: "#ffffff" }}>
              Add Transcript
            </Button>
          )}
          {activeStep === 3 && (
            <Button variant="contained" onClick={handlePublish}
              style={{ background: "linear-gradient(135deg, #1a237e, #0d47a1)", color: "#ffffff" }}>
              Publish Module
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit Module Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#1a237e" }}>Edit Module</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 1 }}>
            <TextField label="Module Title" value={editForm.module_title || ""}
              onChange={e => setEditForm({...editForm, module_title: e.target.value})}
              required sx={{ gridColumn: "span 2" }} />
            <TextField label="Description" value={editForm.description || ""}
              onChange={e => setEditForm({...editForm, description: e.target.value})}
              multiline rows={2} sx={{ gridColumn: "span 2" }} />
            <TextField label="Sequence Order" type="number" value={editForm.sequence_order || 1}
              onChange={e => setEditForm({...editForm, sequence_order: parseInt(e.target.value) || 1})} />
            <TextField label="Pass Threshold %" type="number" value={editForm.pass_threshold || 80}
              onChange={e => setEditForm({...editForm, pass_threshold: parseFloat(e.target.value) || 80})} />
            <TextField label="Max Retakes" type="number" value={editForm.max_retakes || 3}
              onChange={e => setEditForm({...editForm, max_retakes: parseInt(e.target.value) || 3})} />
            <TextField label="Cooldown Hours" type="number" value={editForm.cooldown_hours || 24}
              onChange={e => setEditForm({...editForm, cooldown_hours: parseInt(e.target.value) || 24})} />
            <FormControl size="small" sx={{ gridColumn: "span 2" }}>
              <InputLabel>Status</InputLabel>
              <Select value={editForm.status || "draft"} label="Status"
                onChange={e => setEditForm({...editForm, status: e.target.value})}>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} color="#1a237e">Training Media</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" color="text.secondary">Edit URL</Typography>
                <Switch checked={uploadNewVideo} onChange={e => setUploadNewVideo(e.target.checked)} size="small" />
                <Typography variant="body2" color="text.secondary">Upload New</Typography>
              </Box>
            </Box>
            {editModule?.video && !uploadNewVideo && (
              <Box sx={{ mb: 2, p: 2, bgcolor: "#f5f7fa", borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Current: <b>{editModule.video.video_title}</b> ({editModule.video.media_type || "video"})
                </Typography>
                <Typography variant="caption" color="text.secondary">{editModule.video.storage_url}</Typography>
              </Box>
            )}
            {uploadNewVideo ? (
              <VideoUpload
                moduleId={editModule?.module_id}
                onUploadComplete={(data) => {
                  setSuccess("File uploaded successfully!");
                  setUploadNewVideo(false);
                  setEditVideoForm({...editVideoForm, storage_url: data.storage_url, video_title: data.video_title});
                  loadModules(selectedPath);
                }}
              />
            ) : (
              <Box>
                <Typography variant="body2" color="text.secondary" mb={1}>Or update URL manually:</Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                  <TextField label="Media Title" value={editVideoForm.video_title || ""}
                    onChange={e => setEditVideoForm({...editVideoForm, video_title: e.target.value})}
                    sx={{ gridColumn: "span 2" }} />
                  <TextField label="URL" value={editVideoForm.storage_url || ""}
                    onChange={e => setEditVideoForm({...editVideoForm, storage_url: e.target.value})}
                    sx={{ gridColumn: "span 2" }}
                    helperText="e.g. https://youtube.com/... or /uploads/videos/file.mp4" />
                </Box>
              </Box>
            )}
          </Box>
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} mb={1} color="#1a237e">Transcript</Typography>
            <Button variant="outlined" component="label" size="small" sx={{ mb: 1 }}>
              Upload Transcript File (.txt, .docx, .pdf)
              <input type="file" hidden accept=".txt,.pdf,.doc,.docx"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  if (file.name.endsWith('.txt')) {
                    const text = await file.text();
                    setEditTranscriptForm({...editTranscriptForm, content: text});
                    return;
                  }
                  try {
                    const token = localStorage.getItem("token");
                    const formData = new FormData();
                    formData.append("file", file);
                    const moduleId = editModule?.module_id;
                    const res = await fetch(`/api/v1/upload/transcript/${moduleId}`, {
                      method: "POST",
                      headers: { "Authorization": `Bearer ${token}` },
                      body: formData
                    });
                    if (res.ok) {
                      const data = await res.json();
                      setEditTranscriptForm({...editTranscriptForm, content: data.content});
                      setSuccess(`Transcript extracted from ${file.name}`);
                    } else {
                      setError("Could not extract text. Please paste content manually.");
                    }
                  } catch {
                    setError("Upload failed. Please try again.");
                  }
                }} />
            </Button>
            <TextField fullWidth label="Transcript Content" value={editTranscriptForm.content || ""}
              onChange={e => setEditTranscriptForm({...editTranscriptForm, content: e.target.value})}
              multiline rows={6} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveEdit}
            sx={{ background: "linear-gradient(135deg, #1a237e, #0d47a1)" }}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
